import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MonthlyInvoice, BILLING_PENDING_DATE, BILLING_NEVER_BILLED_DATE } from '../../../../../express-tRPC/src/api/monthly_invoices/types';
import { TableLazyLoadEvent } from 'primeng/table';
import { TrpcService } from '../../services/trpc/trpc.service';
import { Message, MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { ButtonSeverity } from 'src/app/types/common';
import { OverlayPanel } from 'primeng/overlaypanel';
import { DebugSettingsService } from '../../services/debug-settings/debug-settings.service';
import { SUREPARKGroup, SUREPARKSubscriber, State } from '../../../../../express-tRPC/src/shared/types';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { LotSettings } from '../../../../../express-tRPC/src/api/lot-settings/types';

@Component({
  selector: 'app-monthly-invoice-list',
  templateUrl: './monthly-invoice-list.component.html',
  styleUrl: './monthly-invoice-list.component.scss'
})
export class MonthlyInvoiceListComponent {
  invoices: MonthlyInvoice[] = [];
  totalInvoices: number = 0;
  isInvoicelistLoading: boolean = false;
  env = environment;
  siteUrl = environment.SITE_URL;
  lastQueryParams: TableLazyLoadEvent;
  // Stores the location ID the user is currently signed into
  locationId: string = '';
  // Monthly invoices pending count
  monthlyInvoicesPendingCount: number = 0;
  isProcessingMonthlyInvoices: boolean = false;
  isQueueingInvoices: boolean = false;
  isValidatingInvoices: boolean = false;

  // Individual invoice processing
  selectedInvoices: MonthlyInvoice[] = [];
  isProcessingIndividualInvoices: boolean = false;
  invoiceProcessingResult: Message[] = [];
  invoiceProcessingStatuses: Map<string, 'pending' | 'processing' | 'completed' | 'error'> = new Map();
  invoiceProcessingErrors: Map<string, string> = new Map();
  processedInvoicesCount: number = 0;
  totalInvoicesToProcess: number = 0;
  queueMonth: number = new Date().getMonth() + 1;
  queueYear: number = new Date().getFullYear();
  dryRunMode: boolean = false;
  showOnlyOkToSend: boolean = false;
  hiddenRowsCount: number = 0;
  originalInvoices: MonthlyInvoice[] = [];
  searchTerm: string = '';

  activeInvoice: MonthlyInvoice | null = null;
  reason: string = '';
  filterContacts: string = '';
  showDebugControls: boolean = false;
  isLPRActive: boolean = false;
  isCheckingLPRStatus: boolean = true;

  allMissingSureparkProfilesInPpro: SUREPARKGroup[] = [];
  missingSureparkProfilesInPpro:  SUREPARKGroup[] = [];
  selectedSureparkProfiles: SUREPARKGroup[] = [];

  sureparkProfilesSubscribers: SUREPARKSubscriber[] = [];
  selectedSureparkProfilesSubscribers: SUREPARKSubscriber[] = [];

  tenantProfilesData: any[] = []
  isMonthlyProfilesSaving: boolean = false;

  totalTenantProfileSaved: number = 0;
  tenantProfileSavedPercent: number = 0;
  tenantProfileSaveFailed: number = 0;

  showAddMissingProfilesDialog: boolean = false;

  currentStepIndex: number = 0;
  parkProProfileDetailsFormGroup!: FormGroup;
  zipCodeRegex = /^(?:\d{5}|\d{5}-\d{4})$/;
  states: State[];

  selectedTenantProfileFormGroupIndex: number | undefined = undefined;
  showAddBillingContactDialog: boolean = false;
  saveBillingContactFormSubmitted: boolean = false;
  tenantProfileDetailsFormSubmitted: boolean = false;

  hideEmptySureparkGroups: boolean = true;

  @ViewChild('op') op!: OverlayPanel;

  displayDetailedLogsDialog: boolean = false;

  constructor(
    private trpcService: TrpcService,
    private messageService: MessageService,
    private activatedRoute: ActivatedRoute,
    private debugSettingsService: DebugSettingsService
  ) { }

  ngOnInit() {
    // Check debug settings
    this.showDebugControls = this.debugSettingsService.isMonthlyBillingDebugEnabled();

    // Subscribe to query params to get filter-contacts parameter
    this.activatedRoute.queryParams.subscribe(params => {
      this.filterContacts = params['filter-contacts'] || '';
      // Re-apply filter if we have data
      if (this.originalInvoices.length > 0) {
        this.applyAllFilters();
      }
    });

    // Get location ID from session and check LPR status
    this.trpcService.trpc.lots.getSessionLID.query().then((location: string) => {
      this.locationId = location;

      // Check LPR status
      this.checkLPRStatus().then(async () => {
        // Only proceed if LPR is active
        if (this.isLPRActive) {
          // Get the pending monthly invoices count
          await this.getSettings();
          this.getMonthlyInvoicesPendingCount();
          this.getMissingSureparkGroups();
          this.initparkProProfileDetailsForm();
          this.getStates();
        }
      });
    }).catch((error: any) => {
      console.error('Error getting location ID:', error);
    });
  }

  initparkProProfileDetailsForm(): void {
    this.parkProProfileDetailsFormGroup = new FormGroup({
      tenantProfiles: new FormArray([])
    });
  }

  async getStates(): Promise<void> {
    let statesList: State[] | void = await this.trpcService.trpc.lots.getStates.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.states = statesList || [];
  }

  get TenantProfileFormControlArray() {
    return this.parkProProfileDetailsFormGroup.get('tenantProfiles') as FormArray;
  }

  getTenantProfileControlError(index: number, field: string) {
    return this.getTenantProfileFormGroup(index).controls[field].errors;
  }

  getTenantProfileFormGroup(index: number): FormGroup {
    return this.TenantProfileFormControlArray.controls[index] as FormGroup;
  }

  removeTenantProfileGroup(index: number): void {
    this.TenantProfileFormControlArray.removeAt(index);
  }

  addTenantProfileGroup(diasbled: boolean = true, isNew: boolean = true): void {
    this.TenantProfileFormControlArray.push(new FormGroup({
      surepark_group_id: new FormControl<string | null>('', [Validators.required]),
      surepark_group_name: new FormControl<string | null>('', [Validators.required]),
      suite: new FormControl<string | null>('', [Validators.required]),
      account: new FormControl<string | null>('', [Validators.required]),
      name: new FormControl<string | null>('', [Validators.required]),
      billto: new FormControl<string | null>('', [Validators.required]),
      email: new FormControl<string | null>("", [Validators.email]),
      address1: new FormControl<string | null>("", [Validators.required]),
      address2: new FormControl<string | null>("", []),
      city: new FormControl<string | null>("", [Validators.required]),
      state: new FormControl<string | null>("", []),
      zip: new FormControl<string | null>("", [Validators.required, Validators.pattern(this.zipCodeRegex)]),
      invoice_delivery: new FormControl<string | null>("", [])
    }));

  }

  async getInvoices(event?: TableLazyLoadEvent) {

    let limit = event?.rows || 10;
    let offset = event?.first || 0;

    let queryParams = {
      limit: limit,
      offset: offset,
      sortField: ((event?.sortField as string) || ''),
      sortOrder: (event?.sortOrder || 0)
    };

    this.lastQueryParams = {
      rows: queryParams.limit,
      first: queryParams.offset,
      sortField: queryParams.sortField,
      sortOrder: (queryParams?.sortOrder || 0)
    }

    this.isInvoicelistLoading = true;
    let invoicesResult: any = await this.trpcService.trpc.monthly_invoice.getMonthlyInvoices.query(queryParams).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    // Store original data and apply filter if needed
    if (invoicesResult) {
      // Add a unique identifier to each invoice for table selection
      this.originalInvoices = invoicesResult.map((invoice: MonthlyInvoice) => ({
        ...invoice,
        uniqueId: `${invoice.suite}-${invoice.account}`
      }));
      this.applyAllFilters();
    } else {
      this.originalInvoices = [];
      this.invoices = [];
    }

    if (invoicesResult) {
      let count: any = await this.trpcService.trpc.monthly_invoice.getMonthlyInvoicesCount.query().catch((error: any) => {
        this.messageService.add({
          severity: "error",
          detail: error.message,
        });
      });
      this.totalInvoices = count?.length ? count[0]['COUNT(*)'] : 0;
    }
    this.isInvoicelistLoading = false;
  }

  getRequiredTdClassForError(invoice: MonthlyInvoice) {
    if(invoice.issue_description) {
      return ['border-none', 'pb-0'];
    }
    return [];
  }

  handleTenantStatusClick(event: Event, invoice: MonthlyInvoice, overlay: any) {
    if (invoice.tenant_status === 'show' ||
        invoice.tenant_status === '' ||
        invoice.tenant_status === 'active') {

        this.activeInvoice = invoice;
        this.reason = '';
        overlay.toggle(event);
    } else {
      this.updateTenantStatus(invoice.tenant_status, String(invoice.suite), String(invoice.account));
    }
  }

  getTenantStatusLabel(tenantStatus: string): string {
    return tenantStatus == 'show' || tenantStatus == '' ? 'active' : tenantStatus;
  }

  async updateTenantStatus(tenantStatus: string, suite: string | number, account: string | number, reason: string = '') {

    this.op.hide();

    // If tenant was pending deactivation so re-enable
    let newStatus: string = 'active';

    if (tenantStatus == '' || tenantStatus == 'show' || tenantStatus == 'active') {
      // Tenant is active and new status should be deactive.
      newStatus = 'pending deactivation';
    }

    this.isInvoicelistLoading = true;
    let tenantsResult: any = await this.trpcService.trpc.monthly_invoice.toggleTenantStatus.mutate({
      status: newStatus,
      suite: String(suite),
      account: String(account),
      reason: reason
    }).then(() => {
      this.getInvoices(this.lastQueryParams);
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.isInvoicelistLoading = false;
    });

  }

  async updateBillStatus(billStatus: string, suite: string, account: string, reason: string = '', deactiveKeycards: boolean = false) {
    console.log(billStatus, suite, account, reason, deactiveKeycards)

    // If bill status was on hold so re-enable
    let newStatus: string = 'send';

    if (billStatus == '' || billStatus == 'send' || billStatus == 'requires attention') {
      // Bill status is send or requires attention and new status should be hold.
      newStatus = 'hold';
    }

    this.isInvoicelistLoading = true;
    let tenantsResult: any = await this.trpcService.trpc.monthly_invoice.toggleBillStatus.mutate({
      status: newStatus,
      suite: suite,
      account: account,
      reason: reason,
      deactiveKeycards: deactiveKeycards
    }).then(() => {
      this.getInvoices(this.lastQueryParams);
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.isInvoicelistLoading = false;
    });
  }

  async getMissingSureparkGroups() {
    try {
      const result = await this.trpcService.trpc.monthly_invoice.getMissingSureparkGroups.query();
      this.allMissingSureparkProfilesInPpro = result;
      this.filterMissingSureparkGroups();

    } catch (error: any) {
      console.error('Error getting missing tenant profiles:', error);
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to get missing tenant profiles",
      });
    }
  }

  filterMissingSureparkGroups() {
    let filteredMissingSureparkProfilesInPpro: SUREPARKGroup[] = [];

    if(this.hideEmptySureparkGroups) {
      filteredMissingSureparkProfilesInPpro = structuredClone(this.allMissingSureparkProfilesInPpro.filter((group) => group.members?.length));
    } else {
      filteredMissingSureparkProfilesInPpro = structuredClone(this.allMissingSureparkProfilesInPpro);
    }
    this.missingSureparkProfilesInPpro = filteredMissingSureparkProfilesInPpro

  }

  async getSettings() {

    let locationSettings: any = await this.trpcService.trpc.settings.get.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    if (locationSettings) {
      const settings: LotSettings = locationSettings;
      this.hideEmptySureparkGroups = settings.hideEmptySureparkGroups;
    }

  }

  async getMonthlyInvoicesPendingCount() {
    try {
      const result = await this.trpcService.trpc.invoice.getMonthlyInvoicesCount.query();
      console.log(result);
      this.monthlyInvoicesPendingCount = result.count;
    } catch (error: any) {
      console.error('Error getting monthly invoices count:', error);
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to get pending invoices count",
      });
    }
  }

  async processMonthlyInvoices() {
    if (this.isProcessingMonthlyInvoices || this.isProcessingIndividualInvoices) {
      return;
    }

    // Select all invoices that are eligible for processing
    // Filter invoices that are ready to send (not on hold)
    const eligibleInvoices = this.invoices.filter(invoice =>
      !invoice.bill_status ||
      invoice.bill_status === 'send' ||
      invoice.bill_status === 'requires attention'
    );

    if (eligibleInvoices.length === 0) {
      this.messageService.add({
        severity: "warn",
        summary: "No Eligible Invoices",
        detail: "No invoices are eligible for processing. Check if invoices are on hold or already processed.",
      });
      return;
    }

    // Set the selected invoices to all eligible invoices
    this.selectedInvoices = eligibleInvoices;

    // Now call the one-by-one processing function
    await this.processMonthlyInvoicesOneByOne();
  }

  async processMonthlyInvoicesOneByOne() {

    this.messageService.clear();
    this.invoiceProcessingResult = [];

    if (this.isProcessingIndividualInvoices || this.selectedInvoices.length === 0) {
      return;
    }

    const mode = this.dryRunMode ? 'DRY RUN mode' : 'NORMAL mode';
    const monthName = new Date(this.queueYear, this.queueMonth - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const confirmMessage = `Are you sure you want to process ${this.selectedInvoices.length} selected invoice(s) individually in ${mode} for ${monthName}?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    this.isProcessingIndividualInvoices = true;
    this.processedInvoicesCount = 0;
    this.totalInvoicesToProcess = this.selectedInvoices.length;

    // Clear previous statuses and errors
    this.invoiceProcessingStatuses.clear();
    this.invoiceProcessingErrors.clear();

    // Set all selected invoices to pending status
    for (const invoice of this.selectedInvoices) {
      const key = `${invoice.suite}-${invoice.account}`;
      this.invoiceProcessingStatuses.set(key, 'pending');
    }

    try {
      // Process each invoice individually
      for (const invoice of this.selectedInvoices) {
        const key = `${invoice.suite}-${invoice.account}`;

        // Update status to processing
        this.invoiceProcessingStatuses.set(key, 'processing');

        try {
          // Call the backend to process this single invoice
          const result = await this.trpcService.trpc.invoice.processMonthlyInvoicesOneByOne.mutate({
            invoices: [{
              suite: Number(invoice.suite),
              account: Number(invoice.account)
            }],
            dryRun: this.dryRunMode,
            month: Number(this.queueMonth),
            year: Number(this.queueYear)
          });

          // Check the result for this specific invoice
          if (result.success && result.results && result.results.length > 0) {
            const invoiceResult = result.results[0];

            this.invoiceProcessingResult.push({
              summary: key,
              severity: invoiceResult.status == "error" ? 'error' : 'success',
              detail: invoiceResult.status == "error" ? (invoiceResult.error || 'Unknown error') : 'Invoice processed successfully.',
              closable: false
            });

            if (invoiceResult.status === 'completed') {
              this.invoiceProcessingStatuses.set(key, 'completed');
            } else {
              this.invoiceProcessingStatuses.set(key, 'error');
              this.invoiceProcessingErrors.set(key, invoiceResult.error || 'Unknown error');
            }
          }

          this.processedInvoicesCount++;

        } catch (error: any) {
          console.error(`Error processing invoice ${key}:`, error);
          this.invoiceProcessingStatuses.set(key, 'error');
          this.invoiceProcessingErrors.set(key, error.message || 'Unknown error');
          this.processedInvoicesCount++;

          this.invoiceProcessingResult.push({
              summary: key,
              severity: 'error',
              detail: error.message || 'Unknown error'
            });
        }
      }

      // Show summary message
      const completed = Array.from(this.invoiceProcessingStatuses.values()).filter(s => s === 'completed').length;
      const failed = Array.from(this.invoiceProcessingStatuses.values()).filter(s => s === 'error').length;

      const messagePrefix = this.dryRunMode ? '[DRY RUN] ' : '';
      this.messageService.add({
        severity: failed > 0 ? "warn" : "success",
        summary: "Processing Complete",
        detail: `${messagePrefix}Processed ${this.selectedInvoices.length} invoice(s): ${completed} successful, ${failed} failed`,
        key: 'monthly-invoice-result',
        sticky: true,
        closable: false
      });

      // Refresh the count and the table (count shouldn't change in dry run)
      await this.getMonthlyInvoicesPendingCount();
      if (!this.dryRunMode) {
        await this.getInvoices(this.lastQueryParams);

        // Refresh invoice cache after processing completes
        try {
          console.log('Refreshing invoice cache after processing...');
          await this.trpcService.trpc.cache.refreshCache.mutate({
            type: 'invoices',
            lid: this.locationId,
            suite: '',
            account: '',
            when: 'immediate'
          });
          console.log('Invoice cache refresh queued successfully');
        } catch (cacheError: any) {
          // Non-blocking - warn but don't fail the whole operation
          console.warn('Warning: Failed to refresh invoice cache:', cacheError);
          this.messageService.add({
            severity: "warn",
            summary: "Cache Refresh Warning",
            detail: "Invoices processed successfully but cache refresh failed. Data may not be immediately visible.",
          });
        }
      }

    } catch (error: any) {
      console.error('Error in batch processing:', error);
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to process invoices",
      });
    } finally {
      this.isProcessingIndividualInvoices = false;
      // Clear selections after processing
      this.selectedInvoices = [];
      // Clear statuses after a delay to let user see final results
      // setTimeout(() => {
      //   this.invoiceProcessingStatuses.clear();
      //   this.invoiceProcessingErrors.clear();
      // }, 5000);
    }
  }

  openDetailedLogsDialog() {
    this.displayDetailedLogsDialog = true;
  }

  getMessages() {

  }

  getInvoiceProcessingStatus(invoice: MonthlyInvoice): 'pending' | 'processing' | 'completed' | 'error' | null {
    const key = `${invoice.suite}-${invoice.account}`;
    return this.invoiceProcessingStatuses.get(key) || null;
  }

  getInvoiceProcessingError(invoice: MonthlyInvoice): string | undefined {
    const key = `${invoice.suite}-${invoice.account}`;
    return this.invoiceProcessingErrors.get(key);
  }

  async queueMonthlyInvoices() {
    if (this.isQueueingInvoices) {
      return;
    }

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[this.queueMonth - 1];

    const confirmMessage = `Are you sure you want to queue monthly invoices for ${monthName} ${this.queueYear}?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    this.isQueueingInvoices = true;

    try {
      const result = await this.trpcService.trpc.invoice.queueMonthlyInvoices.mutate({
        month: Number(this.queueMonth),
        year: this.queueYear
      });

      if (result.success) {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: `Queued ${result.queued} invoice(s) for ${monthName} ${this.queueYear}`,
        });

        // Refresh the count and the table
        await this.getMonthlyInvoicesPendingCount();
        await this.getInvoices(this.lastQueryParams);
      }
    } catch (error: any) {
      console.error('Error queueing monthly invoices:', error);
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to queue monthly invoices",
      });
    } finally {
      this.isQueueingInvoices = false;
    }
  }

  async clearLastBilled() {
    const confirmMessage = `Are you sure you want to clear the last_billed field for location ${this.locationId}? This will allow monthly invoices to be processed again.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await this.trpcService.trpc.monthly_invoice.clearLastBilled.mutate({
        lid: this.locationId
      });

      if (result.success) {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: `Cleared last_billed for ${result.affectedRows} record(s) in location ${this.locationId}`,
        });

        // Refresh the table to reflect changes
        await this.getInvoices(this.lastQueryParams);
      }
    } catch (error: any) {
      console.error('Error clearing last billed:', error);
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to clear last billed",
      });
    }
  }

  applyOkToSendFilter() {
    this.applyAllFilters();
  }

  applySearchFilter() {
    this.applyAllFilters();
  }

  private applyAllFilters() {
    let filteredInvoices = [...this.originalInvoices];

    // Apply filter-contacts parameter filter
    if (this.filterContacts === 'active-profiles') {
      // Filter to show only invoices that are NOT on hold
      // Include: empty, 'send', and 'requires attention' statuses
      // Exclude: 'hold' status
      filteredInvoices = filteredInvoices.filter(invoice =>
        !invoice.bill_status ||
        invoice.bill_status === 'send' ||
        invoice.bill_status === 'requires attention'
      );
    }

    // Apply the existing showOnlyOkToSend filter
    if (this.showOnlyOkToSend) {
      // Filter to show only invoices with bill_status = 'ok to send'
      filteredInvoices = filteredInvoices.filter(invoice =>
        invoice.bill_status === 'send' || !invoice.bill_status
      );
    }

    // Apply search term filter
    if (this.searchTerm && this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filteredInvoices = filteredInvoices.filter(invoice => {
        const suite = String(invoice.suite || '').toLowerCase();
        const account = String(invoice.account || '').toLowerCase();
        const tenantName = String(invoice.tenant_name || '').toLowerCase();
        const amount = String(invoice.amt || '');
        const tenantStatus = String(invoice.tenant_status || '').toLowerCase();
        const billStatus = String(invoice.bill_status || '').toLowerCase();
        const billStatusReason = String(invoice.bill_status_reason || '').toLowerCase();

        return suite.includes(searchLower) ||
               account.includes(searchLower) ||
               tenantName.includes(searchLower) ||
               amount.includes(searchLower) ||
               tenantStatus.includes(searchLower) ||
               billStatus.includes(searchLower) ||
               billStatusReason.includes(searchLower);
      });
    }

    this.invoices = filteredInvoices;
    this.hiddenRowsCount = this.originalInvoices.length - this.invoices.length;
  }

  /**
   * Determines the display text for the last_billed field based on invoice status
   * @param invoice - The invoice object
   * @returns The appropriate display text for last billed status
   */
  getLastBilledDisplay(invoice: MonthlyInvoice): string {
    if (!invoice.last_refreshed) {
      return 'refreshing';
    }
    if (!invoice.last_billed) {
      return 'invalid';
    }

    // Extract date portion (YYYY-MM-DD) for comparison
    // Handles both MySQL format (1972-02-02 00:00:00) and ISO format (1972-02-02T08:00:00.000Z)
    const billedDatePart = invoice.last_billed.substring(0, 10);
    const pendingDatePart = BILLING_PENDING_DATE.substring(0, 10);
    const neverBilledDatePart = BILLING_NEVER_BILLED_DATE.substring(0, 10);

    if (billedDatePart === pendingDatePart) {
      return 'pending';
    }
    if (billedDatePart === neverBilledDatePart) {
      return 'never';
    }
    // Return formatted date
    return new Date(invoice.last_billed).toLocaleDateString();
  }

  /**
   * Determines the display label for bill status button based on invoice status
   * @param billStatus - The bill status from the invoice
   * @returns The appropriate label text
   */
  getBillStatusLabel(billStatus: string): string {
    // If bill_status is empty or 'send', show 'ok to send'
    if (!billStatus || billStatus === 'send') {
      return 'ok to send';
    }
    // If bill_status is 'requires attention', show that specific status
    // This is set when LPR issues are detected
    if (billStatus === 'requires attention') {
      return 'requires attention';
    }
    // For any other status (like 'hold'), show 'on hold'
    return 'on hold';
  }

  /**
   * Determines the button severity/color based on bill status
   * @param billStatus - The bill status from the invoice
   * @returns The PrimeNG severity level for button styling
   */
  getBillStatusSeverity(billStatus: string): ButtonSeverity {
    // Use warning color for 'requires attention' to highlight issues
    if (billStatus === 'requires attention') {
      return 'warning';
    }
    // Use default secondary color for all other statuses
    return 'secondary';
  }

  /**
   * Handles the bill status button click event
   * @param event - The click event
   * @param invoice - The invoice object
   * @param overlayPanel - Reference to the overlay panel for 'ok to send' or 'requires attention' status
   */
  onBillStatusClick(event: Event, invoice: any, overlayPanel: any): void {
    // If bill_status is empty, 'send', or 'requires attention', show the overlay panel for putting on hold
    if (!invoice.bill_status || invoice.bill_status === 'send' || invoice.bill_status === 'requires attention') {
      overlayPanel.toggle(event);
    } else {
      // For 'hold' status, directly call updateBillStatus to toggle back to 'send'
      this.updateBillStatus(invoice.bill_status, invoice.suite, invoice.account);
    }
  }

  async validateInvoicesReadyToSend() {
    if (this.isValidatingInvoices) {
      return;
    }

    this.isValidatingInvoices = true;

    try {
      // Call the new validation endpoint
      const result = await this.trpcService.trpc.monthly_invoice.validateInvoicesReadyToSend.query();

      // Refresh the invoice list to show any updated issue descriptions
      await this.getInvoices(this.lastQueryParams);

      // Display the validation results
      if (result.invoicesWithIssues > 0) {
        // Build detailed message for issues
        let details = `Validation found issues with ${result.invoicesWithIssues} invoice(s) out of ${result.totalInvoices} checked.\n\n`;

        result.issues.forEach((issue: any) => {
          details += `${issue.suite}-${issue.account} (${issue.tenant_name}):\n`;
          if (issue.error) {
            details += `  - Error: ${issue.error}\n`;
          } else {
            if (issue.missingSureparkMembers > 0) {
              details += `  - Missing SUREPARK members: ${issue.missingSureparkMembers}\n`;
            }
            if (issue.incompleteSureparkMembers > 0) {
              details += `  - Incomplete members (wrong rate): ${issue.incompleteSureparkMembers}\n`;
            }
          }
        });

        this.messageService.add({
          severity: "warn",
          summary: "Validation Issues Found",
          detail: `Found ${result.invoicesWithIssues} invoice(s) with issues. Check server console for details.`,
          sticky: true
        });

        // Log to browser console as well
        console.warn('Invoice validation issues:', result);
      } else if (result.totalInvoices === 0) {
        this.messageService.add({
          severity: "info",
          summary: "No Invoices to Validate",
          detail: "No invoices are currently marked as 'ready to send'.",
        });
      } else {
        this.messageService.add({
          severity: "success",
          summary: "Validation Successful",
          detail: `All ${result.totalInvoices} invoice(s) ready to send validated successfully.`,
        });
      }
    } catch (error: any) {
      console.error('Error validating invoices:', error);
      this.messageService.add({
        severity: "error",
        summary: "Validation Error",
        detail: error.message || "Failed to validate invoices ready to send",
      });
    } finally {
      this.isValidatingInvoices = false;
    }
  }

  async checkLPRStatus() {
    try {
      this.isCheckingLPRStatus = true;
      const result = await this.trpcService.trpc.monthly_invoice.checkLPRStatus.query();
      this.isLPRActive = result.isLPRActive;
    } catch (error: any) {
      console.error('Error checking LPR status:', error);
      // Default to not active if there's an error
      this.isLPRActive = false;
    } finally {
      this.isCheckingLPRStatus = false;
    }
  }

  openAddMissingProfileDialog() {
    this.isMonthlyProfilesSaving = false;
    this.currentStepIndex = 0;
    this.showAddMissingProfilesDialog = true;
  }

  async closeMissingProfileDialog() {
    this.isMonthlyProfilesSaving = false;
    this.showAddMissingProfilesDialog = false;
    this.selectedSureparkProfiles = [];
    this.selectedSureparkProfilesSubscribers = [];
    this.selectedTenantProfileFormGroupIndex = undefined;
    this.tenantProfileSaveFailed = 0;
    this.tenantProfileSavedPercent = 0;
    this.totalTenantProfileSaved = 0;

    //Refresh list after new monthy profile added
    await this.getMonthlyInvoicesPendingCount();
    await this.getInvoices(this.lastQueryParams);
    this.getMissingSureparkGroups();
    this.initparkProProfileDetailsForm();
  }

  goToGroupSelectionStep() {
    this.currentStepIndex = 0;
  }

  goToAddpproDetailsStep() {
    for (let index = 0; index < this.selectedSureparkProfiles.length; index++) {
      const suerparkProfile = this.selectedSureparkProfiles[index];
      if(!this.getTenantProfileFormGroup(index)) {
        this.addTenantProfileGroup();
        setTimeout(() => {
          this.getTenantProfileFormGroup(index).setValue({
            surepark_group_id: suerparkProfile.id,
            surepark_group_name: suerparkProfile.name,
            suite: '',
            account: 'Next',
            name: suerparkProfile.name,
            billto: '',
            email: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            invoice_delivery: 'print'
          })
        }, 0);
        this.getTenantProfileFormGroup(index).get('account')?.disable();
      }
    }

    this.currentStepIndex = 1;
  }

  displayAddBillingContactDialog(formGroupIndex: number) {
    this.selectedTenantProfileFormGroupIndex = formGroupIndex;
    this.showAddBillingContactDialog = true;
  }

  hideBillingContactDialog() {
    this.showAddBillingContactDialog = false;
    this.selectedTenantProfileFormGroupIndex = undefined;
  }

  saveBillingContactDetails() {
    this.saveBillingContactFormSubmitted = true;

    const formGroup = this.getTenantProfileFormGroup(this.selectedTenantProfileFormGroupIndex!);

    if(
      !formGroup.controls['billto'].valid ||
      !formGroup.controls['address1'].valid ||
      !formGroup.controls['address1'].valid ||
      !formGroup.controls['city'].valid ||
      !formGroup.controls['state'].valid||
      !formGroup.controls['zip'].valid
    ) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please fill required fields.'
      });
      return;
    }

    this.saveBillingContactFormSubmitted = false;
    this.hideBillingContactDialog();
  }

  hasBillingContactDetails(formGroupIndex: number, checkValidDetails: boolean = false): boolean {
    const formGroup = this.getTenantProfileFormGroup(formGroupIndex);

    //This flag indicate to check if we have all valid contact details
    if(checkValidDetails) {
      return (
        formGroup.controls['billto'].valid &&
        formGroup.controls['address1'].valid &&
        formGroup.controls['city'].valid &&
        formGroup.controls['zip'].valid
      )
    }

    //Check if we have atleast one required billing contact field detail
    return (
      formGroup.controls['billto'].valid ||
      formGroup.controls['address1'].valid ||
      formGroup.controls['city'].valid ||
      formGroup.controls['zip'].valid
    )
  }

  goToContactSelectionStep() {
    if(this.currentStepIndex === 1) {

      this.tenantProfileDetailsFormSubmitted = true;

      if(!this.parkProProfileDetailsFormGroup.valid) {
        this.messageService.add({
          severity: 'error',
          detail: 'Please fill required fields.'
        });
        return;
      }
      this.tenantProfileDetailsFormSubmitted = false;
    }

    this.sureparkProfilesSubscribers = this.selectedSureparkProfiles.flatMap(item => item.members || []);

    this.currentStepIndex = 2;
  }

  async processTenantAccounts() {

    this.currentStepIndex = 3;

    this.isMonthlyProfilesSaving = true;

    this.tenantProfilesData = this.parkProProfileDetailsFormGroup.getRawValue()['tenantProfiles'];

    for (let index = 0; index < this.tenantProfilesData.length; index++) {
      const tenantProfileData = structuredClone(this.tenantProfilesData[index]);
      tenantProfileData['state'] = tenantProfileData['state']['code'];
      tenantProfileData['contacts'] = this.selectedSureparkProfilesSubscribers.filter((member) => member.groupId == tenantProfileData['surepark_group_id']);
      console.log(tenantProfileData);
      await this.saveTenantProfile(tenantProfileData)
    }

    this.messageService.add({
      severity: "success",
      detail: "Tenant Import Process Completed Successfully.",
      key: 'contact-profile-complete'
    });

    setTimeout(() => {
      this.isMonthlyProfilesSaving = false;
    }, 3000);

  }

  async saveTenantProfile(tenantProfileData: any) {
    await this.trpcService.trpc.monthly_invoice.saveTenantProfile.mutate(tenantProfileData).then((saveTenantProfileResult) => {

      this.totalTenantProfileSaved += 1;
      this.tenantProfileSavedPercent = Math.round(100 / this.tenantProfilesData.length * this.totalTenantProfileSaved);

    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
        key: 'contact-profile-errors'
      });
      this.tenantProfileSaveFailed += 1;
    });
  }

  getGroupMembers(group: SUREPARKGroup, findActive: boolean | undefined = undefined) {
    return group.members?.filter((member) => {
      if(findActive !== undefined) {
        if(findActive === true) {
          return member.active;
        }
        return !member.active;
      }
      return true;

    })
  }
}
